import type { ICard } from '@/entities/client.entity';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, CreditCardIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

const money = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

interface ClientCardsProps {
	cards: ICard[];
	onCopy: (text: string) => void;
}

function CardWithBalanceRow({ card, onCopy }: { card: ICard; onCopy: (text: string) => void }) {
	return (
		<div className='flex items-center justify-between gap-2 min-h-7'>
			<button
				type='button'
				className='text-sm truncate hover:bg-accent active:bg-accent rounded-xs px-0.5 cursor-pointer select-none opacity-80'
				onClick={() => onCopy(card?.number ?? '')}
			>
				{card?.number ?? '—'}
			</button>
			<div className='flex items-center gap-1.5 shrink-0'>
				<button
					type='button'
					className='text-sm tabular-nums text-destructive shrink-0 hover:bg-accent active:bg-accent rounded-xs px-0.5 cursor-pointer select-none'
					onClick={() => onCopy(String(card.balance))}
				>
					{money.format(card.balance)}
				</button>
				{card.balanceDate ? (
					<span className='text-xs text-muted-foreground tabular-nums'>| {card.balanceDate}</span>
				) : null}
			</div>
		</div>
	);
}

function CardWithoutBalanceRow({ card, onCopy }: { card: ICard; onCopy: (text: string) => void }) {
	return (
		<div className='flex items-center min-h-7'>
			<button
				type='button'
				className='text-sm truncate hover:bg-accent active:bg-accent rounded-xs px-0.5 cursor-pointer select-none opacity-80'
				onClick={() => onCopy(card?.number ?? '')}
			>
				{card?.number ?? '—'}
			</button>
		</div>
	);
}

export default function ClientCards({ cards, onCopy }: ClientCardsProps) {
	const [noBalanceOpen, setNoBalanceOpen] = useState(false);

	const { cardsWithBalance, cardsWithoutBalance } = useMemo(() => {
		const withBalance: ICard[] = [];
		const withoutBalance: ICard[] = [];

		for (const card of cards) {
			if (card?.balance) {
				withBalance.push(card);
			} else {
				withoutBalance.push(card);
			}
		}

		withBalance.sort((a, b) => (b.balance ?? 0) - (a.balance ?? 0));

		return { cardsWithBalance: withBalance, cardsWithoutBalance: withoutBalance };
	}, [cards]);

	return (
		<div className='flex flex-col gap-1'>
			<div className='flex items-center gap-1'>
				<CreditCardIcon size={14} className='opacity-40' />
				<span className='text-sm text-muted-foreground'>Карты</span>
			</div>
			<div className='flex flex-col gap-0.5 pl-0.5'>
				{cards.length ? (
					<>
						{cardsWithBalance.map((card, i) => (
							<CardWithBalanceRow key={card?.id ?? i} card={card} onCopy={onCopy} />
						))}
						{cardsWithoutBalance.length > 0 ? (
							<div className='grid gap-0.5'>
								<button
									type='button'
									onClick={() => setNoBalanceOpen(v => !v)}
									className='flex items-center gap-1 h-7 text-sm text-muted-foreground hover:text-foreground transition-colors'
								>
									<ChevronDownIcon size={14} className={cn('transition-transform', noBalanceOpen && 'rotate-180')} />
									Без баланса ({cardsWithoutBalance.length})
								</button>
								{noBalanceOpen
									? cardsWithoutBalance.map((card, i) => (
											<CardWithoutBalanceRow key={card?.id ?? i} card={card} onCopy={onCopy} />
										))
									: null}
							</div>
						) : null}
					</>
				) : (
					<span className='opacity-60 text-sm'>Нет карт</span>
				)}
			</div>
		</div>
	);
}
